import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import DashboardContent from './components/DashboardContent';
import { checkSession, startSessionMonitoring, formatTimeRemaining } from '../../utils/sessionManager';
import { getUserByPhone } from '../../utils/sharedStorage';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

export default function TableauDeBord() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const hasLoadedRef = useRef(false);
  const navigationLockRef = useRef(false);
  const isMountedRef = useRef(true);

  const [activeSection, setActiveSection] = useState('dashboard');
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [activationDetected, setActivationDetected] = useState(false);
  const [deactivationDetected, setDeactivationDetected] = useState(false);
  const [deactivationCountdown, setDeactivationCountdown] = useState(5);

  const safeNavigate = useCallback((path: string) => {
    if (navigationLockRef.current) return;
    if (!isMountedRef.current) return;
    navigationLockRef.current = true;
    setTimeout(() => {
      if (isMountedRef.current) {
        navigateRef.current(path, { replace: true });
      }
      navigationLockRef.current = false;
    }, 100);
  }, []);

  const loadCurrentUser = useCallback(async () => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const raw = localStorage.getItem('currentUser');
    if (!raw) { safeNavigate('/connexion'); return; }

    let userData: any;
    try { userData = JSON.parse(raw); } catch { safeNavigate('/connexion'); return; }

    const phone = userData?.phone;
    if (!phone) { safeNavigate('/connexion'); return; }

    const isLocallyActivated =
      userData.isActivated === true ||
      userData.accountStatus === 'active';

    if (!isLocallyActivated) {
      // Vérifier dans localStorage si l'utilisateur a été activé entre-temps
      try {
        const userKey = `user_${phone}`;
        const storedRaw = localStorage.getItem(userKey);
        if (storedRaw) {
          const stored = JSON.parse(storedRaw);
          const storedActivated = stored.isActivated === true || stored.accountStatus === 'active';
          if (storedActivated) {
            const merged = { ...userData, ...stored, isActivated: true, accountStatus: 'active' };
            localStorage.setItem('currentUser', JSON.stringify(merged));
            if (isMountedRef.current) setCurrentUser(merged);
            return;
          }
          if (stored.blocked) {
            localStorage.removeItem('currentUser');
            safeNavigate('/connexion');
            return;
          }
        }
      } catch { /* ignore */ }

      try {
        const fresh = await getUserByPhone(phone);
        if (fresh) {
          const freshActivated = fresh.isActivated === true || fresh.accountStatus === 'active';
          if (freshActivated) {
            const merged = { ...userData, ...fresh, isActivated: true, accountStatus: 'active' };
            localStorage.setItem('currentUser', JSON.stringify(merged));
            if (isMountedRef.current) setCurrentUser(merged);
            return;
          }
          if (fresh.blocked) {
            localStorage.removeItem('currentUser');
            safeNavigate('/connexion');
            return;
          }
        }
      } catch { /* indisponible */ }

      safeNavigate('/paiement-activation');
      return;
    }

    if (isMountedRef.current) setCurrentUser(userData);

    try {
      const fresh = await getUserByPhone(phone);
      if (fresh && isMountedRef.current) {
        if (fresh.blocked) {
          localStorage.removeItem('currentUser');
          safeNavigate('/connexion');
          return;
        }
        const merged = { ...userData, ...fresh };
        localStorage.setItem('currentUser', JSON.stringify(merged));
        if (isMountedRef.current) setCurrentUser(merged);
      }
    } catch { /* indisponible */ }
  }, [safeNavigate]);

  // ── Initialisation unique au montage ──────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    navigationLockRef.current = false;
    hasLoadedRef.current = false;

    let cancelled = false;

    const init = async () => {
      const session = checkSession();
      if (!session.isValid) {
        if (!cancelled) safeNavigate('/connexion');
        return;
      }
      if (!cancelled && isMountedRef.current) setSessionInfo(session);

      if (session.isAdmin) {
        if (!cancelled && isMountedRef.current) {
          setCurrentUser({ phone: session.userId, name: 'Administrateur', isActivated: true, isAdmin: true });
          setIsAdminMode(true);
        }
      } else {
        if (!cancelled) await loadCurrentUser();
        if (!cancelled && isMountedRef.current) setIsAdminMode(false);
      }
    };

    init();

    const stopMonitoring = startSessionMonitoring(() => {
      if (!cancelled) safeNavigate('/connexion');
    });

    return () => {
      cancelled = true;
      isMountedRef.current = false;
      stopMonitoring();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Abonnement Supabase Realtime sur l'utilisateur connecté ───────────
  useEffect(() => {
    if (isAdminMode || !currentUser?.phone || !isSupabaseConfigured) return;

    const userPhone = currentUser.phone;

    // Abonnement Realtime sur la table users pour détecter les changements
    const channel = supabase
      .channel(`user_${userPhone}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users',
          filter: `phone=eq.${userPhone}`,
        },
        (payload) => {
          console.log('🔄 Changement détecté sur l\'utilisateur:', payload);

          if (payload.eventType === 'UPDATE') {
            const newData = payload.new as any;

            // Détecter activation
            if (newData.accountStatus === 'active' && newData.isActivated === true) {
              const updated = { ...currentUser, ...newData, isActivated: true, accountStatus: 'active' };
              localStorage.setItem('currentUser', JSON.stringify(updated));
              localStorage.setItem(`user_${userPhone}`, JSON.stringify(updated));
              setCurrentUser(updated);
              setActivationDetected(true);
              setTimeout(() => setActivationDetected(false), 6000);

              // Notifier via BroadcastChannel
              try {
                const ch = new BroadcastChannel('account_activation');
                ch.postMessage({ phone: userPhone, activated: true });
                ch.close();
              } catch { /* ignore */ }
            }

            // Détecter désactivation
            if (newData.accountStatus === 'inactive' || newData.isActivated === false) {
              triggerDeactivation(currentUser, userPhone);
            }

            // Détecter blocage
            if (newData.blocked === true) {
              localStorage.removeItem('currentUser');
              safeNavigate('/connexion');
            }

            // Mettre à jour le solde en temps réel
            if (typeof newData.balance === 'number') {
              const updated = { ...currentUser, balance: newData.balance };
              localStorage.setItem('currentUser', JSON.stringify(updated));
              localStorage.setItem(`user_${userPhone}`, JSON.stringify(updated));
              setCurrentUser(updated);

              // Notifier les autres composants
              try {
                const ch = new BroadcastChannel('balance_update');
                ch.postMessage({ phone: userPhone, newBalance: newData.balance });
                ch.close();
              } catch { /* ignore */ }
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdminMode, currentUser?.phone, safeNavigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Polling : détecte activation / désactivation (fallback localStorage) ───
  useEffect(() => {
    if (isAdminMode || !currentUser) return;

    const checkAccountStatus = () => {
      try {
        const userRaw = localStorage.getItem('currentUser');
        if (!userRaw) return;
        const user = JSON.parse(userRaw);
        const phone = user?.phone;
        if (!phone) return;

        // Signal de désactivation
        const deactivationSignalRaw = localStorage.getItem(`deactivation_signal_${phone}`);
        if (deactivationSignalRaw) {
          try {
            const signal = JSON.parse(deactivationSignalRaw);
            if (signal?.deactivated) {
              localStorage.removeItem(`deactivation_signal_${phone}`);
              triggerDeactivation(user, phone);
              return;
            }
          } catch { /* ignore */ }
        }

        // Signal d'activation
        const signalRaw = localStorage.getItem(`activation_signal_${phone}`);
        if (signalRaw) {
          try {
            const signal = JSON.parse(signalRaw);
            if (signal?.activated) {
              localStorage.removeItem(`activation_signal_${phone}`);
              const updated = { ...user, isActivated: true, accountStatus: 'active' };
              localStorage.setItem('currentUser', JSON.stringify(updated));
              localStorage.setItem(`user_${phone}`, JSON.stringify(updated));
              setCurrentUser(updated);
              setActivationDetected(true);
              setTimeout(() => setActivationDetected(false), 6000);
              return;
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };

    const interval = setInterval(checkAccountStatus, 3000);
    checkAccountStatus();
    return () => clearInterval(interval);
  }, [isAdminMode, currentUser?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerDeactivation = (user: any, phone: string) => {
    const updated = { ...user, accountStatus: 'inactive', isActivated: false };
    localStorage.setItem('currentUser', JSON.stringify(updated));
    setCurrentUser(updated);
    setDeactivationDetected(true);
    let count = 5;
    setDeactivationCountdown(count);
    const countdown = setInterval(() => {
      count -= 1;
      setDeactivationCountdown(count);
      if (count <= 0) {
        clearInterval(countdown);
        localStorage.removeItem('currentUser');
        sessionStorage.clear();
        navigationLockRef.current = false;
        navigateRef.current('/connexion', { replace: true });
      }
    }, 1000);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm font-medium">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Bandeau session */}
      <div className="fixed top-0 left-0 right-0 h-9 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-4 flex items-center justify-between z-50 shadow-sm">
        <div className="flex items-center gap-2">
          <i className="ri-shield-check-line text-sm"></i>
          <span className="text-xs font-semibold tracking-wide">Session sécurisée</span>
          {sessionInfo?.isAdmin && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium ml-1">Mode Admin</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {sessionInfo?.timeRemaining && (
            <span className="text-white/75 text-xs hidden sm:block">
              Expire dans {formatTimeRemaining(sessionInfo.timeRemaining)}
            </span>
          )}
          {isAdminMode && (
            <button
              onClick={() => navigateRef.current('/admin')}
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Panel admin
            </button>
          )}
        </div>
      </div>

      {/* Bannière DÉSACTIVATION */}
      {deactivationDetected && (
        <div className="fixed top-9 left-0 right-0 z-40 bg-red-600 text-white px-4 py-4 flex items-center justify-center gap-3 shadow-lg">
          <i className="ri-error-warning-fill text-2xl animate-pulse"></i>
          <div className="text-center">
            <p className="text-sm font-bold">⛔ Votre compte a été désactivé par l&apos;administrateur.</p>
            <p className="text-xs mt-1 text-red-100">Déconnexion dans <strong>{deactivationCountdown}</strong> seconde{deactivationCountdown > 1 ? 's' : ''}...</p>
          </div>
        </div>
      )}

      {/* Bannière ACTIVATION */}
      {activationDetected && (
        <div className="fixed top-9 left-0 right-0 z-40 bg-green-500 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg">
          <i className="ri-checkbox-circle-fill text-xl"></i>
          <span className="text-sm font-bold">🎉 Votre compte vient d&apos;être activé ! Bienvenue sur votre tableau de bord.</span>
        </div>
      )}

      <Sidebar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        currentSection={activeSection}
        setCurrentSection={setActiveSection}
      />

      <div className="lg:pl-64 pt-9 min-h-screen flex flex-col">
        <DashboardContent
          currentSection={activeSection}
          setIsSidebarOpen={setIsOpen}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
}